import {
  Box,
  Table,
  Td,
  Th,
  Thead,
  Tr,
  Flex,
  Text,
  Input,
  Tbody,
} from '@chakra-ui/react';
import { getIn } from 'formik';
import * as _ from 'lodash';
import React, { useState, useCallback } from 'react';

import { FormulaEngine } from '@finley-technologies/finley-types';

import { EditIntermediateFieldModal } from './EditIntermediateField';
import { useDraggableRow } from 'packages/common-frontend/components/DragAndDropTable/hooks';
import * as DragAndDropTableTypes from 'packages/common-frontend/components/DragAndDropTable/types';
import { TrDraggableHandle } from 'packages/common-frontend/components/DragAndDropTable/ui';
import {
  AddIconButton,
  DeleteIconButton,
  EditIconButton,
} from 'packages/common-frontend/components/IconButton';
import {
  CreditAgreement,
  getIterationKey,
} from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { WipConfig } from 'packages/creditAgreements/ui/types';

const createIntermediateFieldShell = (
  idx: number,
): CreditAgreement.Config.Liabilities.Inputs.IntermediateField => ({
  field: `fieldName${idx}`,
  config: {
    formula: 'placeHolder',
    type: 'formula',
    engineVersion: FormulaEngine.SupportedFormulaEngines.HyperFormula1,
  },
});

const IntermediateFieldView = ({
  wipConfig,
  intermediateField,
  basePropertyKey,
  configPropertyKey,
  fullPropertyKey,
  idx,
  moveRow,
  isDragDisabled,
  id,
}: {
  intermediateField: CreditAgreement.Config.Liabilities.Inputs.IntermediateField;
  wipConfig?: WipConfig;
  configPropertyKey: string;
  basePropertyKey: string;
  fullPropertyKey: string;
  idx: number;
  moveRow: DragAndDropTableTypes.MoveRow;
  isDragDisabled: boolean;
  id: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const onSave = useCallback(
    (
      field: CreditAgreement.Config.Liabilities.Inputs.IntermediateField,
      saveIdx: number,
    ) => {
      // trim leading "=", formula engine does not detect this error until runtime.
      const sanitizedField: CreditAgreement.Config.Liabilities.Inputs.IntermediateField = field.config.formula.startsWith(
        '=',
      )
        ? {
            ...field,
            config: { ...field.config, formula: field.config.formula.slice(1) },
          }
        : field;

      if (wipConfig) {
        const config = _.cloneDeep(
          getIn(wipConfig.formProps.values, configPropertyKey), // liabilityAccount[idx]._config_.configurations[configIdx]
        ) as CreditAgreement.Config.Liabilities.Inputs.Config;

        config.intermediateFields = config.intermediateFields.map(
          (intField, index) => (index === saveIdx ? sanitizedField : intField),
        );

        // TODO: Validate config with trigger date

        wipConfig.formProps.setFieldValue(fullPropertyKey, field);
      }
    },
    [wipConfig, fullPropertyKey, configPropertyKey],
  );

  const deleteIntermediateField = (wc: WipConfig) => {
    // getIn always returns any, needs typecast to filter
    const fields = getIn(
      wc.formProps.values,
      basePropertyKey,
    ) as CreditAgreement.Config.Liabilities.Inputs.IntermediateField[];

    wc.formProps.setFieldValue(
      basePropertyKey,
      fields.filter((_unused, varIdx) => varIdx !== idx),
    );
  };

  // if (intermediateField.field === 'accrualDate') return null;

  const requiredFields: string[] =
    CreditAgreement.Config.Liabilities.Inputs.RequiredIntermediateFieldIterable
      .options;
  const disableDelete = requiredFields.includes(intermediateField.field);
  const disableEdit = intermediateField.field === 'accrualDate';

  return (
    <TrDraggableHandle
      index={idx}
      id={id}
      acceptedType="intermediateField"
      moveRow={moveRow}
      isDragDisabled={isDragDisabled}
    >
      <Td w="40">{intermediateField.field}</Td>
      <Td w="12">{intermediateField.config.type}</Td>
      <Td w="65%">
        <Input
          p="0"
          value={intermediateField.config.formula}
          readOnly
          border="none"
        />
      </Td>
      {wipConfig?.isMutateAllowed(fullPropertyKey) && isDragDisabled && (
        <Td>
          <EditIntermediateFieldModal
            isOpen={isOpen}
            onCancel={() => setIsOpen(false)}
            onSave={onSave}
            intermediateField={intermediateField}
            idx={idx}
          />
          <EditIconButton
            ariaLabel="edit field"
            label="Edit Field"
            onClick={() => {
              setIsOpen(true);
            }}
            isDisabled={disableEdit}
          />

          <DeleteIconButton
            ariaLabel="delete field"
            label="Delete Field"
            onClick={() => {
              deleteIntermediateField(wipConfig);
            }}
            isDisabled={disableDelete}
          />
        </Td>
      )}
    </TrDraggableHandle>
  );
};

export const IntermediateFieldsView = ({
  wipConfig,
  basePropertyKey,
  configPropertyKey,
  intermediateFields,
}: {
  intermediateFields: CreditAgreement.Config.Liabilities.Inputs.IntermediateField[];
  wipConfig?: WipConfig;
  basePropertyKey: string;
  configPropertyKey: string;
}) => {
  const {
    useRowState,
    useOrderLockedState,
    moveRow,
    ui: { LockOrderButton, RestoreOrderButton, SaveOrderCallout },
  } = useDraggableRow<CreditAgreement.Config.Liabilities.Inputs.IntermediateField>(
    { comparisonKey: 'field' },
  );
  const [isOrderLocked] = useOrderLockedState();
  const [intermediateFieldsState] = useRowState(intermediateFields);

  const onSaveOrder = useCallback(() => {
    wipConfig?.formProps.setFieldValue(
      basePropertyKey,
      intermediateFieldsState,
    );
  }, [basePropertyKey, intermediateFieldsState, wipConfig]);

  const addIntermediateField = (wc: WipConfig) => {
    const fields =
      _.cloneDeep(getIn(wc.formProps.values, basePropertyKey)) ?? [];

    const field = createIntermediateFieldShell(fields.length);

    fields.unshift(field);

    wc.formProps.setFieldValue(basePropertyKey, fields);
  };

  return (
    <Flex flexDirection="column" mb="3">
      <Box mb="5">
        <Flex alignItems="center">
          <Text fontWeight="bold" fontSize="large" mr="2">
            Intermediate Fields
          </Text>
          {wipConfig?.isMutateAllowed(basePropertyKey) && (
            <>
              {isOrderLocked && (
                <AddIconButton
                  ariaLabel="add intermeidate field"
                  label="Add Intermediate Field"
                  onClick={() => {
                    addIntermediateField(wipConfig);
                  }}
                />
              )}
              <LockOrderButton
                onSaveOrder={onSaveOrder}
                isDisabled={!wipConfig.isMutateAllowed(basePropertyKey)}
              />
              <RestoreOrderButton />
            </>
          )}
        </Flex>

        {wipConfig?.isMutateAllowed(basePropertyKey) && (
          <>
            <SaveOrderCallout />
            <Text fontStyle="italic" fontSize="xs">
              &quot;accrualDate&quot; intermediate field is configured by
              default.
            </Text>
          </>
        )}
      </Box>

      {intermediateFields.length === 0 && (
        <Text p="3" border="1px" borderColor="gray.700">
          There are currently no intermediate fields.
        </Text>
      )}
      {intermediateFields.length > 0 && (
        <Table borderWidth="1px">
          <Thead>
            <Tr>
              {wipConfig && !isOrderLocked && <Th w="10" />}
              <Th>Field Name</Th>
              <Th>Type</Th>
              <Th>Formula</Th>
              {wipConfig && isOrderLocked && <Th>Edit/Delete</Th>}
            </Tr>
          </Thead>
          <Tbody>
            {intermediateFieldsState.map((intermediateField, idx) => {
              const id = getIterationKey(intermediateField);
              return (
                <IntermediateFieldView
                  key={id}
                  configPropertyKey={configPropertyKey}
                  intermediateField={intermediateField}
                  wipConfig={wipConfig}
                  basePropertyKey={basePropertyKey}
                  fullPropertyKey={`${basePropertyKey}[${idx}]`}
                  idx={idx}
                  moveRow={moveRow}
                  isDragDisabled={isOrderLocked}
                  id={id}
                />
              );
            })}
          </Tbody>
        </Table>
      )}
    </Flex>
  );
};
